import { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/lib/api";
import { NewsletterComposer } from "@/components/NewsletterComposer";

/**
 * AdminNewsletter — read-only list of newsletter subscribers with:
 *  · search
 *  · one-click CSV export (email, source, created_at, unsubscribed_at)
 *  · quick "copy all emails to clipboard" helper for pasting into
 *    Mailchimp / Substack / Resend Audiences later
 */
const AdminNewsletter = () => {
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState("");
  const [copyState, setCopyState] = useState("idle");

  useEffect(() => {
    adminApi.get("/admin/newsletter").then((r) => setSubs(r.data || []));
  }, []);

  const filtered = useMemo(() => {
    if (!subs) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return subs;
    return subs.filter((s) =>
      (s.email || "").toLowerCase().includes(needle) ||
      (s.source || "").toLowerCase().includes(needle)
    );
  }, [subs, q]);

  const exportCsv = () => {
    if (!subs?.length) return;
    const rows = [
      ["email", "source", "created_at", "unsubscribed_at"],
      ...subs.map((s) => [
        s.email || "",
        s.source || "",
        s.created_at || "",
        s.unsubscribed_at || "",
      ]),
    ];
    const csv = rows
      .map((r) =>
        r
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `goya-newsletter-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyEmails = async () => {
    if (!subs?.length) return;
    const text = subs.map((s) => s.email).filter(Boolean).join(", ");
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch (_e) {
      setCopyState("error");
      setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  if (subs === null) {
    return <div className="p-10 text-sm text-[#8A7568]" data-testid="admin-newsletter-loading">Loading subscribers…</div>;
  }

  return (
    <div className="p-6 md:p-10" data-testid="admin-newsletter-root">
      <NewsletterComposer activeCount={subs.filter((s) => !s.unsubscribed_at).length} />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-[#241C18]">Newsletter subscribers</h2>
          <p className="mt-1 text-sm text-[#8A7568]">
            <span data-testid="admin-newsletter-count">{subs.length}</span> subscriber{subs.length === 1 ? "" : "s"} · stored in your MongoDB Atlas
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="admin-newsletter-copy"
            onClick={copyEmails}
            disabled={!subs.length}
            className="px-4 py-2 rounded-full border border-[#F1DCC5] text-[13px] font-badge tracking-[0.14em] uppercase hover:bg-[#F1DCC5]/50 disabled:opacity-40"
          >
            {copyState === "copied" ? "Copied ✓" : copyState === "error" ? "Copy failed" : "Copy emails"}
          </button>
          <button
            data-testid="admin-newsletter-export"
            onClick={exportCsv}
            disabled={!subs.length}
            className="px-4 py-2 rounded-full bg-[var(--gl-orange)] text-[var(--gl-orange-text)] text-[13px] font-badge tracking-[0.14em] uppercase hover:opacity-90 disabled:opacity-40"
          >
            Export CSV
          </button>
        </div>
      </div>

      <input
        data-testid="admin-newsletter-search"
        placeholder="Filter by email or source…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full md:w-96 px-4 py-2 rounded-full border border-[#F1DCC5] bg-white outline-none focus:border-[var(--gl-orange)] mb-4"
      />

      {filtered.length === 0 ? (
        <div className="py-16 text-center font-serif-display italic text-xl text-[#8A7568]" data-testid="admin-newsletter-empty">
          {subs.length === 0
            ? "No subscribers yet — the newsletter block is live on the footer."
            : "No matches for that filter."}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#F1DCC5] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F1E6] text-[10px] uppercase tracking-[0.2em] text-[#8A7568]">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Unsubscribed</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id || s.email} className="border-t border-[#F1DCC5]" data-testid={`admin-newsletter-row-${s.email}`}>
                  <td className="px-4 py-3 font-typewriter text-[13px] text-[#241C18]">{s.email}</td>
                  <td className="px-4 py-3 text-[#8A7568]">{s.source || "—"}</td>
                  <td className="px-4 py-3 text-[#8A7568]">{(s.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-[#8A7568]">
                    {s.unsubscribed_at ? <span className="text-[#C97A63]">yes</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletter;
